<template>
  <div class="flex justify-center">
    <div v-if="store.isLoading" class="items-center py-64 justify-center">
      <div class="flex py-4 items-center justify-center space-x-2">
        <div class="w-4 h-4 rounded-full animate-pulse bg-dbs-green"></div>
        <div class="w-4 h-4 rounded-full animate-pulse bg-dbs-green"></div>
        <div class="w-4 h-4 rounded-full animate-pulse bg-dbs-green"></div>
      </div>
      <div class="flex justify-center items-center space-x-1 text-l">
        <span class="text-dbs-navy font-medium text-lg"
          >Getting everything ready...</span
        >
      </div>
    </div>
    <div v-else-if="store.meterData" class="container mx-auto lg:px-72">
      <!-- ! Beta warning -->
      <!-- <div class="pt-10"></div>
      <div
        class="bg-red-100 rounded-lg py-5 px-6 mb-4 text-base text-red"
        role="alert"
      >
        <strong>Closed Beta Testing</strong> - This page is currently under
        heavy development and is not yet ready for public use.
      </div> -->
      <!-- * Meter Header and basic info -->
      <div class="pt-10">
        <header-section />
      </div>
      <div class="py-2">
        <strong>Select Year</strong>
      </div>
      <div class="py-2">
        <button
          v-for="(item, index) in store.meterData.years"
          :key="index"
          @click="updateTimeframe(item), scrollToGraphs()"
          class="rounded mr-2 px-6 py-2.5 bg-dbs-green text-white font-medium leading-tight uppercase hover:shadow-lg hover:shadow-dbs-green-dropshadow hover:ease-in hover:bg-dbs-green-accent focus:bg-dbs-green-accent focus:outline-none focus:ring-0 active:bg-dbs-green-accent transition duration-150 ease-in-out"
        >
          {{ item }}
        </button>
      </div>
      <div class="pt-10">
        <!-- * CO2e Calc -->
        <div class="divide-y-[3px] divide-dbs-green">
          <div class="text-xl py-4">
            <strong>Carbon Impact</strong
            ><span class="text-xs font-medium text-dbs-green">
              - {{ data.selectedYear }}</span
            >
          </div>
          <!--  * Consumption insight blurb  -->
          <div class="py-4">
            <div class="pb-2">
              <strong>What are you being shown you here?</strong>
            </div>
            <div class="text-sm">
              This section shows you the carbon intensity of your energy
              consumption*. This carbon impact is calculated by multiplying your
              energy consumption by the UK carbon intensity factor. Carbon
              intensity is measured in grams of carbon dioxide equivalent (CO2e)
              per kilowatt hour (kWh).

              <a
                class="font-bold text-dbs-green"
                href="https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2024"
                target="_blank"
                >UK Gov Greenhouse Gas Reporting 🔗</a
              >
              <br /><span class="text-xs font-medium text-dbs-green-accent"
                >* 2024 UK electricity carbon intensity</span
              >
            </div>
          </div>
        </div>
      </div>
      <div>
        <CarbonIntensity
          :totalConsumption="store.meterData.carbonIntensity[data.selectedYear]"
        />
      </div>
      <!--  * Consumption Insight  -->
      <div class="pt-10">
        <div class="divide-y-[3px] divide-dbs-green">
          <div ref="graphs" class="text-xl py-4">
            <strong>Consumption Insight</strong
            ><span class="text-xs font-medium text-dbs-green">
              - {{ data.selectedYear }}</span
            >
          </div>
          <!--  * Consumption insight blurb  -->
          <div class="py-4">
            <div class="pb-2">
              <strong>What are you being shown you here?</strong>
            </div>
            <div>
              <strong class="text-sm">Monthly kWh Consumption</strong>
              <p class="text-sm">
                This bar chart shows you the total historic kWh consumption
                broken down by month. The data will typically be reflected in
                your monthly energy supplier bills provided the billing date
                aligns with month end.
              </p>
              <strong class="text-sm">Time of Day Consumption</strong>
              <p class="text-sm">
                This is a simple way to appraise potential energy waste
                particularly when considering energy consumed outside of your
                sites normal operating hours. Reducing energy consumption starts
                with identifying when, and where, it could be being wasted.
              </p>
            </div>
          </div>
        </div>
        <!--  * 2 column Graphs  -->
        <div class="columns-2">
          <div>
            <span><strong>Monthly kWh Consumption</strong></span>
          </div>
          <div class="pl-20">
            <span><strong>Time of Day Consumption</strong></span>
          </div>
        </div>
        <div class="columns-2 py-10">
          <div>
            <bar-chart
              :series-data="
                store.meterData.monthlyKWHConsumption[0][data.selectedYear]
              "
              :categories="categories"
            />
          </div>
          <div class="pl-20 py-10">
            <pie-chart
              :series-data="
                store.meterData.timeOfDayConsumption[data.selectedYear]
              "
            />
          </div>
        </div>
      </div>
      <div class="pt-10">
        <div class="divide-y-[3px] divide-dbs-green">
          <div class="text-xl py-4">
            <strong>Mapped Insight</strong
            ><span class="text-xs font-medium text-dbs-green">
              - {{ data.selectedYear }}</span
            >
          </div>
          <!--  * Heatmap insight blurb  -->
          <div class="py-4">
            <div class="pb-2">
              <strong>What are you being shown you here?</strong>
            </div>
            <div class="text-sm">
              <strong>Day/Hour heatmap usage</strong>
              <p>
                This heatmap shows the average energy intensity at hourly
                intervals across a typical day of the week. This is particularly
                useful when overlayed with your operating hours as it provides a
                more granular view of where we might find energy waste and
                therefore opportunities to save. This heatmap also supports an
                initial assessment of site suitability and potential system size
                for renewable generation projects. As different generating
                assets have different generating profiles, knowing when you need
                to use energy aids this initial assessment.
              </p>
            </div>
          </div>
        </div>
        <!-- * Headmps  -->
        <div class="py-2">
          <heatmap-day-trend
            :series-data="store.meterData.timeOfDayHeatmap[data.selectedYear]"
          />
        </div>
        <div class="pt-10">
          <span><strong>Day/Half Hourly Usage</strong></span>
        </div>
        <div>
          <HalfHourTrend
            :series-data="store.meterData.halfHourChart[data.selectedYear]"
          />
        </div>
        <div class="pt-10">
          <HeatmapMonthTrend
            :series-data="store.meterData.timeOfMonthHeatmap[data.selectedYear]"
          />
        </div>
        <div class="pt-10">
          <span><strong>Month/Half Hourly Usage</strong></span>
        </div>
        <div>
          <HalfHourTrendMonth
            :series-data="
              store.meterData.monthlyhalfHourTrendChart[data.selectedYear]
            "
          />
        </div>
      </div>
      <!--  * Peak & Base Insight  -->
      <div class="pt-10">
        <div class="divide-y-[3px] divide-dbs-green">
          <div class="text-xl py-4">
            <strong>Peak & Base Insight</strong
            ><span class="text-xs font-medium text-dbs-green">
              - {{ data.selectedYear }}</span
            >
          </div>
          <!-- * Peak & Base insight blurb  -->
          <div class="py-4">
            <div class="pb-2">
              <strong>What are you being shown you here?</strong>
            </div>
            <div class="text-sm">
              <strong>Comparison Usage</strong>
              <p>
                The graph shows the highest (peak) and lowest (base) energy
                consumption on any given day. Reducing peak demand can
                significantly reduce your energy invoice. This is particularly
                pertinent considering the impact of the
                <a
                  class="font-bold text-dbs-green"
                  href="https://www.nationalgrideso.com/industry-information/charging/transmission-network-use-system-tnuos-charges"
                  target="_blank"
                  >TNUoS</a
                >
                charging methodology changes implemented in April 2023. Base
                demand is the 'always on' energy demand across your estate;
                finding where this energy is consumed, and taking steps to
                reduce it, will deliver a permanent reduction in your energy
                consumption.
              </p>
              <strong>Total Per Day</strong>
              <p>
                This is your total daily energy consumption plotted across a
                12-month period. Useful to assess the energy reduction during
                periods of downtime (planned shutdowns, periods of inactivity),
                does the consumption curve match your expectations? Comparing
                this data to your operational data (things made, customers
                served, revenue generated) provides value insight to your Cost
                of Goods sold and/or Services Provided.
              </p>
            </div>
          </div>
        </div>
        <div class="py-2">
          <LineAreaChart
            :series-data="store.meterData.totalPerDayMinMax[data.selectedYear]"
          />
        </div>
        <div class="py-2">
          <LineChart
            :series-data="store.meterData.totalPerDayChart[data.selectedYear]"
          />
        </div>
      </div>

      <!-- * Trend Insight -->
      <div class="pt-10">
        <div class="divide-y-[3px] divide-dbs-green">
          <div class="text-xl py-4">
            <strong>Other Insights</strong
            ><span class="text-xs font-medium text-dbs-green">
              - {{ data.selectedYear }}</span
            >
          </div>
          <!-- * Trend insight blurb  -->
          <div class="py-4">
            <div class="pb-2">
              <strong>What are you being shown you here?</strong>
            </div>
            <div class="text-sm">
              <strong>Seasonal Trend Consumption</strong>
              <p class="pb-2">
                This graph shows the average time of day energy consumption for
                each season. This is helpful a good way of identifying trends
                and helps us to understand if there is significant cooling
                demand across the summer months, or if a site is electrically
                heated Winter. With analysis of this nature, we are seeking
                trends and anomalies that identify the questions would benefit
                from further investigation.
              </p>
              <ul class="text-sm">
                <li><strong>Spring:</strong> March, April, May</li>
                <li><strong>Summer:</strong> June, July, August</li>
                <li><strong>Autumn:</strong> September, October, November</li>
                <li><strong>Winter:</strong> December, January, February</li>
              </ul>
            </div>
          </div>
        </div>
        <!--  * 2 column Graphs -->
        <div class="pt-10">
          <span><strong>Seasonal Trend Consumption</strong></span>
        </div>
        <div>
          <div>
            <SeasonalTrend
              :series-data="
                store.meterData.seasonalTrendChart[data.selectedYear]
              "
            />
          </div>
        </div>
      </div>
      <div class="py-10"></div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, onMounted, ref } from "vue";
import { DateTime } from "luxon";
import { useAuditStore } from "@/store/index";
import { useRoute } from "vue-router";

import HeaderSection from "@/components/meter-audit/meter-audit-header.vue";
import BarChart from "@/components/meter-audit/bar-chart.vue";
import PieChart from "@/components/meter-audit/pie-chart.vue";
import HeatmapMonthTrend from "@/components/meter-audit/heatmap-month-trend.vue";
import HeatmapDayTrend from "@/components/meter-audit/heatmap-day-trend.vue";
import LineAreaChart from "@/components/meter-audit/line-area-chart.vue";
import LineChart from "@/components/meter-audit/line-chart.vue";
import HalfHourTrend from "@/components/meter-audit/half-hour-trend.vue";
import SeasonalTrend from "@/components/meter-audit/seasonal-trend.vue";
import CarbonIntensity from "@/components/meter-audit/carbon-intensity.vue";
import HalfHourTrendMonth from "@/components/meter-audit/half-hour-trend-month.vue";
import router from "@/router";

/**
 * fancy little function to move the scroll bar to the graphs
 * when you click the year buttons
 */
const graphs = ref(null);
const scrollToGraphs = () => {
  graphs.value.scrollIntoView({ behavior: "smooth" });
};

const data = reactive({
  selectedYear: "Last 12 Month",
  last12Months() {
    const now = DateTime.now().startOf("month");
    let i = 0;
    let months = [];
    while (i <= 12) {
      const month = now.minus({ months: i });
      months.push(month);
      i++;
    }
    return months.reverse();
  },

  defaultCategorys: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ],
});

/**
 * Update the selected timeframe to display in the report
 * @param {string} timeframe - The timeframe to update to
 */
const updateTimeframe = (timeframe) => (data.selectedYear = timeframe);
const store = useAuditStore();
/**
 * Returns categories depending on year type selected
 */
const categories = computed(() => {
  if (data.selectedYear !== "Last 12 Month") {
    return data.defaultCategorys;
  } else {
    return data.last12Months().map((month) => month.toFormat("MMM"));
  }
});

/**
 * Returns Total consumption depending on year type selected
 */
const route = useRoute();

onMounted(async () => {
  // confusion-rack-bishop
  // record-architect-divorce
  await store.collectMeterData(route.params.word_id);
  if (!store.meterData) {
    router.push({
      name: "not-found",
      // preserve current path and remove the first char to avoid the target URL starting with `//`
      params: { pathMatch: route.path.substring(1).split("/") },
    });
  }
});
</script>
